
import { Component, Prop, Vue } from 'vue-property-decorator'
import { FALLBACK_LOCALE } from '@/commons/constants/locale'
import ProgressBar from '@/commons/components/core/ProgressBar.vue'
import { ApplicationType } from '@/commons/constants/application'
import { RouterUtils } from '@/commons/utils/router'
import { AuthenticationService, authenticationStore } from '@/store'
import { ENVIRONMENT } from '@/commons/configuration'
import { PaymentDescriptionTranslationKeys } from '@/commons/types/payment'

@Component({
    components: {
        ProgressBar
    }
})
export default class Success extends Vue {
    @Prop() applicationType!: ApplicationType
    // TODO handling of login or main navigation buttons if already authorized

    created (): void {
        document.title = this.$tc('metadata.confirmation')
    }

    get isExpeditedCard (): boolean {
        return RouterUtils.queryParamToBool(this.$route.query.quickCard)
    }

    get isExpeditedPass (): boolean {
        return RouterUtils.queryParamToBool(this.$route.query.quickPass)
    }

    get isComboApplication (): boolean {
        return this.applicationType === ApplicationType.COMBO
    }

    get description (): string {
        if (this.isCourierDelivery) {
            return this.resolveCourierDescription()
        }
        if (this.isForeignRetrieval) {
            return this.resolveDescription({
                expeditedCombo: 'payment.expeditedCombo',
                expeditedCard: 'payment.expeditedCardApplication',
                expeditedPassport: 'payment.expeditedPassApplication',
                expeditedComboCard: 'payment.expeditedCardOnComboApplicationForeignRetrieval',
                expeditedComboPassport: 'payment.expeditedPassOnComboApplicationForeignRetrieval',
                normal: 'payment.regularApplicationForeignRetrieval',
                normalPartialForeignRetrieval: 'payment.regularApplicationPartialForeignRetrieval'
            })
        } else if (this.isComboApplication) {
            return this.resolveDescription({
                expeditedCombo: 'payment.expeditedComboWithComboNotice',
                expeditedCard: 'payment.expeditedCardApplication',
                expeditedPassport: 'payment.expeditedPassApplication',
                expeditedComboCard: 'payment.expeditedCardOnComboApplicationWithComboNotice',
                expeditedComboPassport: 'payment.expeditedPassOnComboApplicationWithComboNotice',
                normal: 'payment.regularApplicationWithComboNotice'
            })
        } else {
            return this.resolveDescription({
                expeditedCombo: 'payment.expeditedCombo',
                expeditedCard: 'payment.expeditedCardApplication',
                expeditedPassport: 'payment.expeditedPassApplication',
                expeditedComboCard: 'payment.expeditedCardOnComboApplication',
                expeditedComboPassport: 'payment.expeditedPassOnComboApplication',
                normal: 'payment.regularApplication'
            })
        }
    }

    private resolveDescription (translationKeys: PaymentDescriptionTranslationKeys): string {
        let translationKeyReference = 'normal'
        const isComboApplication = this.applicationType === ApplicationType.COMBO
        const idCardForeignDelivery = RouterUtils.queryParamToBool(this.$route.query.idCardForeignRetrieval)
        const passportForeignDelivery = RouterUtils.queryParamToBool(this.$route.query.passportForeignRetrieval)

        if (this.isExpeditedPass && this.isExpeditedCard) {
            translationKeyReference = isComboApplication ? 'expeditedCombo' : 'normal'
        } else if (this.isExpeditedCard) {
            translationKeyReference = isComboApplication ? 'expeditedComboCard' : 'expeditedCard'
        } else if (this.isExpeditedPass) {
            translationKeyReference = isComboApplication ? 'expeditedComboPassport' : 'expeditedPassport'
        } else if (isComboApplication && idCardForeignDelivery && !passportForeignDelivery) {
            translationKeyReference = 'normalPartialForeignRetrieval'
        } else if (isComboApplication && !idCardForeignDelivery && passportForeignDelivery) {
            translationKeyReference = 'normalPartialForeignRetrieval'
        }
        return translationKeys[translationKeyReference]
    }

    private resolveCourierDescription (): string {
        const isComboApplication = this.applicationType === ApplicationType.COMBO
        const idCardForeignDelivery = RouterUtils.queryParamToBool(this.$route.query.idCardForeignRetrieval)
        if (isComboApplication) {
            if (this.isExpeditedCard) {
                return 'payment.courier.expeditedCardOnComboApplication'
            } else if (idCardForeignDelivery) {
                return 'payment.courier.comboApplicationForeignRetrieval'
            } else {
                return 'payment.courier.comboApplication'
            }
        } else {
            return 'payment.courier.regularApplication'
        }
    }

    get trackApplicationUrl (): string {
        return ENVIRONMENT.applicationTrackUrl[this.$i18n.locale] || ENVIRONMENT.applicationTrackUrl[FALLBACK_LOCALE]
    }

    get surveyUrl (): string {
        return ENVIRONMENT.surveyUrl[this.$i18n.locale] || ENVIRONMENT.surveyUrl[FALLBACK_LOCALE]
    }

    get surveyForChildUrl (): string {
        return ENVIRONMENT.surveyForChildUrl[this.$i18n.locale] || ENVIRONMENT.surveyForChildUrl[FALLBACK_LOCALE]
    }

    get reservationUrl (): string {
        return ENVIRONMENT.reservationUrl[this.$i18n.locale] || ENVIRONMENT.reservationUrl[FALLBACK_LOCALE]
    }

    get isForeignRetrieval (): boolean {
        const idCardForeignDelivery = RouterUtils.queryParamToBool(this.$route.query.idCardForeignRetrieval)
        const passportForeignDelivery = RouterUtils.queryParamToBool(this.$route.query.passportForeignRetrieval)
        return idCardForeignDelivery || passportForeignDelivery
    }

    get isCourierDelivery (): boolean {
        return RouterUtils.queryParamToBool(this.$route.query.passportCourier)
    }

    get showReservationLink (): boolean {
        const comboApplication = this.applicationType === ApplicationType.COMBO
        const expeditedCard = this.isExpeditedCard
        const expeditedPass = this.isExpeditedPass
        const idCardForeignDelivery = RouterUtils.queryParamToBool(this.$route.query.idCardForeignRetrieval)
        const passportForeignDelivery = RouterUtils.queryParamToBool(this.$route.query.passportForeignRetrieval)
        const courierDelivery = RouterUtils.queryParamToBool(this.$route.query.passportCourier)
        if (idCardForeignDelivery && passportForeignDelivery && comboApplication) {
            return false
        } else {
            if (!comboApplication && courierDelivery) {
                return false
            }
            if (comboApplication) {
                return this.showReservationLinkWhenCombo(idCardForeignDelivery, expeditedPass, courierDelivery, passportForeignDelivery, expeditedCard)
            } else {
                return !(expeditedCard || expeditedPass || idCardForeignDelivery || passportForeignDelivery)
            }
        }
    }

    get showSurveyForChild (): boolean {
        return RouterUtils.queryParamToBool(this.$route.query.child) &&
            this.applicationType !== ApplicationType.CITIZEN_PASSPORT
    }

    get isAuthenticated (): boolean {
        return authenticationStore.isAuthenticated
    }

    showReservationLinkWhenCombo (idCardForeignDelivery: boolean, expeditedPass: boolean, courierDelivery: boolean, passportForeignDelivery: boolean, expeditedCard: boolean): boolean {
        if (idCardForeignDelivery && (expeditedPass || courierDelivery)) {
            return false
        }
        return !(passportForeignDelivery && expeditedCard)
    }

    async logout (): Promise<void> {
        await AuthenticationService.logout()
        await this.$router.push('/login', RouterUtils.setFocusToBody)
    }
}
